import { useEffect, useMemo, useState } from 'react';

import {
  recentSentimentNews,
  useDashboardStore,
  type RecentNewsForTooltip,
} from '@/state/store';
import type { AgentRuntimeState, PodRuntimeState } from '@/state/store';
import { SIGNAL_BUBBLE_TTL_MS, PM_BUBBLE_TTL_MS } from '@/state/store';
import { useBreakpoint, type Breakpoint } from '@/hooks/useBreakpoint';
import type { EventMood } from '@/events';

/**
 * Pod room — the per-pod "office" sub-view.  Phase 3 mock: pure DOM
 * boxes for the 6 analyst agents, a centered PM speech bubble, a
 * modifiers ribbon and the latest order ticker.  Pixi version
 * eventually replaces the spatial layout but selectors stay the
 * same.
 */

const AGENT_LAYOUT: { actor: string; label: string; row: number; col: number }[] = [
  { actor: 'qa', label: 'Quant', row: 0, col: 0 },
  { actor: 'sa', label: 'Sentiment', row: 0, col: 2 },
  { actor: 'fa', label: 'Fundamental', row: 1, col: 0 },
  { actor: 'researcher', label: 'Researcher', row: 1, col: 2 },
  { actor: 'rm', label: 'Risk Mgr', row: 2, col: 0 },
  { actor: 'ea', label: 'Execution', row: 2, col: 2 },
];

const MOOD_COLOR: Record<EventMood | string, string> = {
  idle: '#8b949e',
  thinking: '#58a6ff',
  confident: '#2ea043',
  worried: '#d29922',
  excited: '#db61a2',
  angry: '#f85149',
  proud: '#a371f7',
  sad: '#6e7681',
  frozen: '#58a6ff',
  celebrating: '#ffd33d',
};

const ROOM_BACKGROUND: Record<string, string> = {
  canary_lab: 'linear-gradient(135deg, #1f3a5f, #161b22)',
  active_offices: 'linear-gradient(135deg, #1f6feb22, #0d1117)',
  reduced_floor: 'linear-gradient(135deg, #d2992233, #161b22)',
  frozen_basement: 'linear-gradient(135deg, #58a6ff44, #0d1117)',
};

const ROOM_LABEL: Record<string, string> = {
  canary_lab: 'Canary Lab',
  active_offices: 'Active Offices',
  reduced_floor: 'Reduced Floor',
  frozen_basement: 'Frozen Basement',
};

export function PodRoomView({ podId }: { podId: string }) {
  const pod = useDashboardStore((s) => s.pods[podId]);
  const podSummary = useDashboardStore((s) =>
    s.building?.floors
      .flatMap((f) => f.pods)
      .find((p) => p.pod_id === podId),
  );
  const goToBuilding = useDashboardStore((s) => s.goToBuilding);
  const bp = useBreakpoint();

  // Esc → back to building view.  Bound once on mount.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') goToBuilding();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goToBuilding]);

  // Re-render every second so bubble fade-out by age stays accurate
  // without requiring an event to arrive.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, []);

  const floor = pod?.floor ?? podSummary?.floor ?? 'active_offices';
  const name = podSummary?.name ?? podId;

  return (
    <section
      style={{
        background: ROOM_BACKGROUND[floor] ?? '#0d1117',
        minHeight: 0,
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',
        overflow: 'hidden',
      }}
    >
      <RoomHeader
        name={name}
        podId={podId}
        floor={floor}
        pod={pod ?? null}
        onBack={goToBuilding}
        bp={bp}
      />
      <RoomFloor pod={pod ?? null} now={now} bp={bp} />
      <RoomFooter pod={pod ?? null} />
    </section>
  );
}

function RoomHeader({
  name,
  podId,
  floor,
  pod,
  onBack,
  bp,
}: {
  name: string;
  podId: string;
  floor: string;
  pod: PodRuntimeState | null;
  onBack: () => void;
  bp: Breakpoint;
}) {
  const riskState = pod?.riskState ?? 'active';
  const isMobile = bp === 'mobile';
  return (
    <header
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: isMobile ? 10 : 16,
        rowGap: 8,
        padding: isMobile ? '10px 14px' : '12px 20px',
        borderBottom: '1px solid #21262d',
        background: '#161b22dd',
        backdropFilter: 'blur(8px)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <button
          onClick={onBack}
          style={{
            background: '#21262d',
            color: '#e6edf3',
            border: '1px solid #30363d',
            borderRadius: 6,
            padding: isMobile ? '8px 12px' : '6px 10px',
            minHeight: isMobile ? 36 : 'auto',
            cursor: 'pointer',
            fontSize: 12,
            touchAction: 'manipulation',
            flexShrink: 0,
          }}
          aria-label="Back to building view (Esc)"
        >
          ← back
        </button>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </div>
          <div style={{ fontSize: 11, color: '#8b949e' }}>
            {podId} · {ROOM_LABEL[floor] ?? floor}
          </div>
        </div>
      </div>
      {!isMobile ? <ModifiersRibbon pod={pod} /> : null}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 11,
          color: '#8b949e',
        }}
      >
        risk:{' '}
        <strong style={{ color: '#e6edf3' }}>
          {riskState}
          {pod?.haircutPct ? ` (haircut ${Math.round(pod.haircutPct * 100)}%)` : ''}
        </strong>
      </div>
      {isMobile ? (
        <div style={{ flexBasis: '100%' }}>
          <ModifiersRibbon pod={pod} />
        </div>
      ) : null}
    </header>
  );
}

function ModifiersRibbon({ pod }: { pod: PodRuntimeState | null }) {
  const utility = pod?.utility;
  const pm = pod?.pm;
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 14,
        rowGap: 4,
        fontSize: 11,
        color: '#8b949e',
        alignItems: 'baseline',
      }}
    >
      {pm && pm.confidence != null && (
        <span>
          PM conf:{' '}
          <strong style={{ color: '#e6edf3' }}>{pm.confidence.toFixed(2)}</strong>
        </span>
      )}
      {pm && pm.targetGross != null && (
        <span>
          target gross:{' '}
          <strong style={{ color: '#e6edf3' }}>{pm.targetGross.toFixed(2)}</strong>
        </span>
      )}
      {pm && pm.intentsCount != null && (
        <span>
          intents:{' '}
          <strong style={{ color: '#e6edf3' }}>{pm.intentsCount}</strong>
        </span>
      )}
      {utility && (
        <span>
          utility:{' '}
          <strong style={{ color: utility.total >= 0 ? '#2ea043' : '#f85149' }}>
            {utility.total >= 0 ? '+' : ''}
            {utility.total.toFixed(3)}
          </strong>
        </span>
      )}
    </div>
  );
}

function RoomFloor({
  pod,
  now,
  bp,
}: {
  pod: PodRuntimeState | null;
  now: number;
  bp: Breakpoint;
}) {
  // News for the SA tooltip: filter the global feed to envelopes
  // mentioning any of this pod's tickers (positions + SA's last
  // signal).  Computed once per render and passed to the SA seat
  // only — the other seats don't need it.
  const feed = useDashboardStore((s) => s.feed);
  const saNews = useMemo(() => {
    const positions = pod?.positions ?? {};
    const topTickers = pod?.agents.sa?.topTickers ?? [];
    const merged = Array.from(new Set([...Object.keys(positions), ...topTickers]));
    return recentSentimentNews(feed, merged, 5);
  }, [feed, pod?.positions, pod?.agents.sa?.topTickers]);

  if (bp === 'mobile') {
    // Mobile: PM at top (sticky-ish), then 2×3 agent grid below.
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          padding: 14,
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        <PmCentre pod={pod} now={now} compact />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 10,
          }}
        >
          {AGENT_LAYOUT.map((spot) => {
            const agent = pod?.agents[spot.actor] ?? null;
            return (
              <AgentSeat
                key={spot.actor}
                actorKey={spot.actor}
                label={spot.label}
                agent={agent}
                now={now}
                news={spot.actor === 'sa' ? saNews : null}
                compact
              />
            );
          })}
        </div>
      </div>
    );
  }
  // Tablet + desktop: original 3-column ring around the PM.
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1.4fr 1fr',
        gridTemplateRows: 'repeat(3, 1fr)',
        gap: 12,
        padding: bp === 'tablet' ? 16 : 24,
        minHeight: 0,
      }}
    >
      {AGENT_LAYOUT.map((spot) => {
        const agent = pod?.agents[spot.actor] ?? null;
        return (
          <AgentSeat
            key={spot.actor}
            actorKey={spot.actor}
            seatRow={spot.row}
            seatCol={spot.col}
            label={spot.label}
            agent={agent}
            now={now}
            news={spot.actor === 'sa' ? saNews : null}
          />
        );
      })}
      <PmCentre pod={pod} now={now} />
    </div>
  );
}

function AgentSeat({
  actorKey,
  seatRow,
  seatCol,
  label,
  agent,
  now,
  news,
  compact = false,
}: {
  actorKey: string;
  seatRow?: number;
  seatCol?: number;
  label: string;
  agent: AgentRuntimeState | null;
  now: number;
  /** When non-null, render a hover popover with these news entries.
   *  Currently only the SA seat sets this. */
  news: RecentNewsForTooltip[] | null;
  compact?: boolean;
}) {
  const mood = agent?.mood ?? 'idle';
  const accent = MOOD_COLOR[mood] ?? '#8b949e';
  const ageMs = agent?.lastUpdatedAt ? now - new Date(agent.lastUpdatedAt).getTime() : Infinity;
  const showBubble = agent && !agent.thinking && ageMs < SIGNAL_BUBBLE_TTL_MS;
  const showThinking = agent?.thinking ?? false;
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      tabIndex={news ? 0 : -1}
      style={{
        gridRow: seatRow != null ? seatRow + 1 : undefined,
        gridColumn: seatCol != null ? seatCol + 1 : undefined,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: compact ? 4 : 6,
        minHeight: 0,
        padding: compact ? '8px 6px' : 0,
        background: compact ? '#0d111766' : 'transparent',
        borderRadius: compact ? 10 : 0,
        border: compact ? '1px solid #21262d' : 'none',
        position: 'relative',
        cursor: news ? 'help' : 'default',
        outline: 'none',
      }}
      aria-describedby={news && hovered ? `sa-news-${actorKey}` : undefined}
    >
      <Bubble
        text={showThinking ? '…' : bubbleText(agent)}
        visible={showThinking || Boolean(showBubble)}
        accent={accent}
        compact={compact}
      />
      <AgentAvatar
        label={label}
        actor={agent?.actor ?? null}
        accent={accent}
        mood={mood}
        compact={compact}
      />
      <AgentStats agent={agent} />
      {news && hovered ? (
        <NewsPopover id={`sa-news-${actorKey}`} items={news} accent={accent} />
      ) : null}
    </div>
  );
}

function newsSentimentDot(value: number): { color: string; glyph: string } {
  if (value > 0.05) return { color: '#2ea043', glyph: '▲' };
  if (value < -0.05) return { color: '#f85149', glyph: '▼' };
  return { color: '#8b949e', glyph: '•' };
}

function NewsPopover({
  id,
  items,
  accent,
}: {
  id: string;
  items: RecentNewsForTooltip[];
  accent: string;
}) {
  return (
    <div
      id={id}
      role="tooltip"
      style={{
        position: 'absolute',
        top: '100%',
        left: '50%',
        transform: 'translate(-50%, 6px)',
        width: 280,
        maxWidth: '90vw',
        background: '#0d1117f2',
        border: `1px solid ${accent}55`,
        borderRadius: 8,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
        backdropFilter: 'blur(4px)',
        padding: '8px 10px',
        fontSize: 11,
        color: '#e6edf3',
        lineHeight: 1.4,
        zIndex: 10,
        pointerEvents: 'none',
        textAlign: 'left',
      }}
    >
      <div
        style={{
          color: '#8b949e',
          fontSize: 9,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          marginBottom: 4,
        }}
      >
        Последние новости
      </div>
      {items.length === 0 ? (
        <div style={{ color: '#6e7681', fontStyle: 'italic', fontSize: 10 }}>
          пока тихо в новостях по нашим эмитентам
        </div>
      ) : (
        items.map((item) => {
          const dot = newsSentimentDot(item.sentiment);
          const ticker = item.tickers[0] ?? '';
          return (
            <div
              key={item.id}
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto auto 1fr',
                gap: 6,
                alignItems: 'baseline',
                fontSize: 10,
                padding: '2px 0',
              }}
            >
              <span style={{ color: dot.color, fontSize: 9 }}>{dot.glyph}</span>
              {ticker ? (
                <span
                  style={{
                    fontFamily: 'monospace',
                    color: dot.color,
                    fontSize: 9,
                    padding: '0 4px',
                    borderRadius: 6,
                    border: `1px solid ${dot.color}`,
                  }}
                >
                  {ticker}
                </span>
              ) : (
                <span />
              )}
              <span
                style={{
                  color: '#c9d1d9',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.title}
              </span>
            </div>
          );
        })
      )}
    </div>
  );
}

function AgentAvatar({
  label,
  actor,
  accent,
  mood,
  compact = false,
}: {
  label: string;
  actor: string | null;
  accent: string;
  mood: EventMood | string;
  compact?: boolean;
}) {
  const code = (actor ?? label).slice(0, 2).toUpperCase();
  const pulsing = mood === 'thinking';
  const size = compact ? 56 : 80;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: compact ? 10 : 14,
        background: `${accent}22`,
        border: `2px solid ${accent}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: compact ? 16 : 22,
        fontWeight: 700,
        color: accent,
        position: 'relative',
        boxShadow: pulsing ? `0 0 14px ${accent}` : 'none',
        animation: pulsing ? 'agentPulse 1.4s ease-in-out infinite' : undefined,
      }}
    >
      {code}
    </div>
  );
}

function AgentStats({ agent }: { agent: AgentRuntimeState | null }) {
  if (!agent || (agent.lastSignal == null && agent.lastConfidence == null)) {
    return <div style={{ fontSize: 10, color: '#6e7681' }}>idle</div>;
  }
  const signal = agent.lastSignal ?? 0;
  return (
    <div
      style={{
        fontSize: 10,
        color: '#8b949e',
        textAlign: 'center',
      }}
    >
      <div>
        signal{' '}
        <strong style={{ color: signal >= 0 ? '#2ea043' : '#f85149' }}>
          {signal >= 0 ? '+' : ''}
          {signal.toFixed(3)}
        </strong>
      </div>
      {agent.lastConfidence != null && (
        <div>
          conf <strong style={{ color: '#e6edf3' }}>{agent.lastConfidence.toFixed(2)}</strong>
        </div>
      )}
    </div>
  );
}

function bubbleText(agent: AgentRuntimeState | null): string {
  if (!agent) return '';
  if (agent.topTickers.length > 0) return agent.topTickers.join(', ');
  return `signal ${(agent.lastSignal ?? 0).toFixed(2)}`;
}

function Bubble({
  text,
  visible,
  accent,
  compact = false,
}: {
  text: string;
  visible: boolean;
  accent: string;
  compact?: boolean;
}) {
  return (
    <div
      style={{
        minHeight: compact ? 20 : 26,
        maxWidth: compact ? 140 : 220,
        padding: visible ? '4px 10px' : '0 10px',
        borderRadius: 12,
        background: visible ? '#0d1117' : 'transparent',
        border: visible ? `1px solid ${accent}` : '1px solid transparent',
        color: visible ? '#e6edf3' : 'transparent',
        fontSize: compact ? 10 : 11,
        textAlign: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        transition: 'opacity .25s ease, max-width .25s ease',
        opacity: visible ? 1 : 0,
        boxShadow: visible ? `0 4px 12px ${accent}33` : 'none',
      }}
    >
      {text || ' '}
    </div>
  );
}

function PmCentre({
  pod,
  now,
  compact = false,
}: {
  pod: PodRuntimeState | null;
  now: number;
  compact?: boolean;
}) {
  const pm = pod?.pm ?? null;
  const ageMs = pm?.decisionAt ? now - new Date(pm.decisionAt).getTime() : Infinity;
  const showBubble = pm && !pm.thinking && ageMs < PM_BUBBLE_TTL_MS;
  const showThinking = pm?.thinking ?? false;
  const accent =
    (pm?.intentsCount === 0 ? MOOD_COLOR.worried : MOOD_COLOR.confident) ?? '#8b949e';
  const summary = pm?.lastUiSummary ?? pm?.lastRationale ?? '';

  // Truncate long rationale into a single-line bubble (the wall NAV
  // chart will show the full rationale in a later phase).
  const truncated = useMemo(() => {
    if (!summary) return '';
    return summary.length > 120 ? `${summary.slice(0, 117)}…` : summary;
  }, [summary]);

  const pmSize = compact ? 84 : 120;
  return (
    <div
      style={{
        gridRow: compact ? 'auto' : '1 / span 3',
        gridColumn: compact ? 'auto' : 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: compact ? 8 : 14,
        padding: compact ? 12 : 0,
        background: compact ? '#0d111766' : 'transparent',
        borderRadius: compact ? 12 : 0,
        border: compact ? `1px solid ${accent}55` : 'none',
      }}
    >
      <Bubble
        text={showThinking ? 'PM analysing…' : truncated || ''}
        visible={showThinking || Boolean(showBubble && truncated)}
        accent={accent}
        compact={compact}
      />
      <div
        style={{
          width: pmSize,
          height: pmSize,
          borderRadius: compact ? 18 : 24,
          background: `${accent}33`,
          border: `3px solid ${accent}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: compact ? 24 : 32,
          fontWeight: 700,
          color: accent,
          boxShadow: showThinking ? `0 0 22px ${accent}` : 'none',
          animation: showThinking ? 'agentPulse 1.4s ease-in-out infinite' : undefined,
        }}
      >
        PM
      </div>
      <div style={{ fontSize: compact ? 10 : 11, color: '#8b949e', textAlign: 'center' }}>
        Portfolio Manager
      </div>
    </div>
  );
}

function RoomFooter({ pod }: { pod: PodRuntimeState | null }) {
  const orders = pod?.recentOrders ?? [];
  if (orders.length === 0) {
    return (
      <footer
        style={{
          padding: '8px 20px',
          borderTop: '1px solid #21262d',
          background: '#0d1117',
          color: '#6e7681',
          fontSize: 11,
        }}
      >
        Trading floor idle — awaiting orders.
      </footer>
    );
  }
  return (
    <footer
      style={{
        padding: '8px 20px',
        borderTop: '1px solid #21262d',
        background: '#0d1117',
        display: 'flex',
        gap: 12,
        overflowX: 'auto',
        whiteSpace: 'nowrap',
      }}
    >
      {orders.map((o) => (
        <span
          key={o.id}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            color: '#e6edf3',
            background: '#161b22',
            border: `1px solid ${
              o.type === 'filled' ? '#2ea043' : o.type === 'rejected' ? '#f85149' : '#30363d'
            }`,
            padding: '4px 10px',
            borderRadius: 14,
          }}
        >
          <strong>{o.ticker || '—'}</strong>
          <span style={{ color: '#8b949e' }}>{o.detail}</span>
        </span>
      ))}
    </footer>
  );
}
